import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from '../components/ui';
import { useTranslation } from '../i18n';
import type { TranslationKey } from '../i18n/keys';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { useStore } from 'react-redux';
import type { RootState } from '../store';
import { fetchCouple } from '../store/slices/coupleSlice';
import { fetchActiveSession } from '../store/slices/gameSlice';
import {
  fetchFantasyAnswers,
  resetFantasyMatch,
} from '../store/slices/fantasyMatchSlice';
import type { CoupleNudgeEvent, NudgeType } from '../types/couple';
import type { FantasyAnswersUpdatedEvent } from '../types/fantasyMatch';
import { disconnectEcho } from './echo';
import { useCoupleChannel } from './useCoupleChannel';

const NUDGE_MESSAGE: Record<NudgeType, TranslationKey> = {
  wave: 'realtime.nudge.wave',
  miss_you: 'realtime.nudge.missYou',
  play_now: 'realtime.nudge.playNow',
};

/**
 * Mounted once inside the toast provider. Loads the couple after sign-in,
 * joins the couple's presence channel (online dot) and turns a partner's
 * nudge into a toast; "Let's play" offers a Join button that opens the
 * partner's live game. Renders nothing.
 */
export function RealtimeBridge() {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { showToast } = useToast();
  const isAuthenticated = useAppSelector(state => state.auth.isAuthenticated);
  const coupleId = useAppSelector(state => state.couple.couple?.id ?? null);
  const partner = useAppSelector(state => state.couple.partner);
  const wasAuthenticatedRef = useRef(false);
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  useEffect(() => {
    if (isAuthenticated) {
      wasAuthenticatedRef.current = true;
      void dispatch(fetchCouple());
    } else if (wasAuthenticatedRef.current) {
      wasAuthenticatedRef.current = false;
      dispatch(resetFantasyMatch());
      disconnectEcho();
    }
  }, [dispatch, isAuthenticated]);

  const openPartnerGame = useCallback(async () => {
    try {
      const session = await dispatch(fetchActiveSession()).unwrap();
      if (session?.play_mode === 'two_devices') {
        navigate(`/game/${session.id}`);
        return;
      }
    } catch {
      // fall through to the message below
    }
    showToast({ message: t('realtime.nudge.noGame') });
  }, [dispatch, navigate, showToast, t]);

  const handleNudge = useCallback(
    (event: CoupleNudgeEvent) => {
      const name = partner?.name || t('common.partner');
      const inGame = pathRef.current.startsWith('/game/');
      if (event.type === 'play_now') {
        showToast({
          message: t(
            inGame ? 'realtime.nudge.inGame' : NUDGE_MESSAGE.play_now,
            {
              name,
            }
          ),
          ...(inGame
            ? {}
            : {
                actionLabel: t('realtime.nudge.join'),
                onAction: () => void openPartnerGame(),
                durationMs: 8000,
              }),
        });
        return;
      }
      showToast({ message: t(NUDGE_MESSAGE[event.type], { name }) });
    },
    [openPartnerGame, partner?.name, showToast, t]
  );

  /**
   * Loads my answers once the pair is known, so the first match that appears
   * while I am elsewhere in the app has a count to be compared with.
   */
  useEffect(() => {
    if (coupleId != null) void dispatch(fetchFantasyAnswers());
  }, [coupleId, dispatch]);

  const handleFantasyAnswers = useCallback(
    (event: FantasyAnswersUpdatedEvent) => {
      const known = store.getState().fantasyMatch.payload?.matches.length;
      void dispatch(fetchFantasyAnswers());
      // Without a loaded list there is nothing to compare the count with.
      if (known !== undefined && event.match_count > known) {
        showToast({
          message: t('realtime.fantasyMatch.new', {
            name: partner?.name || t('common.partner'),
          }),
          actionLabel: t('realtime.fantasyMatch.view'),
          onAction: () => navigate('/fantasy-catalog?tab=matches'),
          durationMs: 6000,
        });
      }
    },
    [dispatch, navigate, partner?.name, showToast, store, t]
  );

  useCoupleChannel({
    coupleId,
    partnerId: partner?.id ?? null,
    onNudge: handleNudge,
    onFantasyAnswers: handleFantasyAnswers,
  });

  return null;
}

export default RealtimeBridge;
